import type React from "react";
import { useTablistNavigation } from "../../../hooks/useTablistNavigation";
import { UI_ICONS } from "../../../icons";

export type PreferenceTab =
	| "general"
	| "apikeys"
	| "data"
	| "advanced"
	| "notifications"
	| "autocomment";
const PREFERENCE_TAB_ORDER: PreferenceTab[] = [
	"general",
	"apikeys",
	"notifications",
	"autocomment",
	"data",
	"advanced",
];
export const PREFERENCE_TAB_PANEL_IDS: Record<PreferenceTab, string> = {
	general: "preference-panel-general",
	apikeys: "preference-panel-apikeys",
	notifications: "preference-panel-notifications",
	autocomment: "preference-panel-autocomment",
	data: "preference-panel-data",
	advanced: "preference-panel-advanced",
};
export const PREFERENCE_TAB_IDS: Record<PreferenceTab, string> = {
	general: "preference-tab-general",
	apikeys: "preference-tab-apikeys",
	notifications: "preference-tab-notifications",
	autocomment: "preference-tab-autocomment",
	data: "preference-tab-data",
	advanced: "preference-tab-advanced",
};

interface PreferenceTabNavProps {
	activeTab: PreferenceTab;
	apiKeyConfigured: boolean;
	onChangeTab: (tab: PreferenceTab) => void;
}

const tabButtonClass = (isActive: boolean) =>
	`px-6 py-3 text-left bg-transparent border-none cursor-pointer text-[0.9rem] transition-all duration-200 whitespace-nowrap flex items-center justify-between gap-2 hover:bg-white/5 hover:text-text-main focus-visible:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary active:scale-95 max-[600px]:px-4 max-[600px]:py-3 max-[600px]:border-b-2 max-[600px]:justify-center ${
		isActive
			? "bg-primary/10 text-primary font-semibold border-r-2 border-primary max-[600px]:border-r-0 max-[600px]:border-b-primary"
			: "text-text-dim border-transparent"
	}`;

export const PreferenceTabNav: React.FC<PreferenceTabNavProps> = ({
	activeTab,
	apiKeyConfigured,
	onChangeTab,
}) => {
	const { getTabProps } = useTablistNavigation({
		tabs: PREFERENCE_TAB_ORDER,
		activeTab,
		onChange: onChangeTab,
	});

	return (
		<div
			className="w-[200px] border-r border-border-main flex flex-col py-4 shrink-0 max-[600px]:w-full max-[600px]:flex-row max-[600px]:overflow-x-auto max-[600px]:border-r-0 max-[600px]:border-b max-[600px]:p-0 max-[600px]:scrollbar-none"
			role="tablist"
			aria-label="設定タブ"
		>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.general}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.general}
				{...getTabProps("general")}
				className={tabButtonClass(activeTab === "general")}
				onClick={() => onChangeTab("general")}
			>
				AI設定
			</button>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.apikeys}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.apikeys}
				{...getTabProps("apikeys")}
				className={tabButtonClass(activeTab === "apikeys")}
				onClick={() => onChangeTab("apikeys")}
			>
				APIキー
				{!apiKeyConfigured ? (
					<span
						className="text-[0.9rem] leading-none filter drop-shadow-[0_0_2px_rgba(245,158,11,0.5)]"
						title="APIキーが未設定です"
						role="img"
						aria-hidden="true"
					>
						{UI_ICONS.WARNING}
					</span>
				) : null}
				{!apiKeyConfigured ? <span className="sr-only">未設定</span> : null}
			</button>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.notifications}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.notifications}
				{...getTabProps("notifications")}
				className={tabButtonClass(activeTab === "notifications")}
				onClick={() => onChangeTab("notifications")}
			>
				通知設定
			</button>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.autocomment}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.autocomment}
				{...getTabProps("autocomment")}
				className={tabButtonClass(activeTab === "autocomment")}
				onClick={() => onChangeTab("autocomment")}
			>
				自動コメント
			</button>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.data}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.data}
				{...getTabProps("data")}
				className={tabButtonClass(activeTab === "data")}
				onClick={() => onChangeTab("data")}
			>
				データ管理
			</button>
			<button
				type="button"
				id={PREFERENCE_TAB_IDS.advanced}
				role="tab"
				aria-controls={PREFERENCE_TAB_PANEL_IDS.advanced}
				{...getTabProps("advanced")}
				className={tabButtonClass(activeTab === "advanced")}
				onClick={() => onChangeTab("advanced")}
			>
				解析設定
			</button>
		</div>
	);
};
